import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { filter, finalize } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import {
  LiveUpdateEventTypes,
  growthIndustryTopic,
  liveStatusTranslationKey
} from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { GrowthAdminService, GrowthIndustryPackResponse } from './growth-admin.service';
import { GROWTH_INDUSTRY_PACK_LIFECYCLE_STATUSES } from './growth-industry-form.models';

@Component({
  standalone: true,
  selector: 'ain-growth-industry-packs',
  imports: [AinPageComponent, InfoHintComponent, TableModule, FormsModule, ButtonModule, InputTextModule, SelectModule, RouterLink],
  template: `
    <ain-page [title]="t('growth.industryPacks.title')" [description]="t('growth.industryPacks.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.industryPacks.help.title'"
        [descriptionKey]="'growth.industryPacks.help.description'"
        [bulletKeys]="['growth.industryPacks.help.bullets.lifecycle', 'growth.industryPacks.help.bullets.live']" />

      <section class="card list-card">
        <div class="toolbar">
          <input
            pInputText
            type="text"
            [(ngModel)]="searchTerm"
            [placeholder]="t('growth.industryPacks.search')" />
          <p-select
            [options]="lifecycleOptions()"
            [(ngModel)]="lifecycleFilter"
            optionLabel="label"
            optionValue="value"
            [showClear]="true"
            appendTo="body"
            [placeholder]="t('growth.industryPacks.filterLifecycle')" />
          <button pButton type="button" [label]="t('growth.industryPacks.search')" (click)="applyFilters()"></button>
          <button pButton type="button" [label]="t('growth.industryPacks.refresh')" severity="secondary" (click)="load()"></button>
          <span class="live-badge">{{ t(liveStatusKey(liveUpdateService.connectionState())) }}</span>
        </div>
        <div class="toolbar">
          <button
            pButton
            type="button"
            [label]="t('growth.industryPacks.actions.openSelected')"
            severity="secondary"
            [disabled]="!selected()"
            (click)="openSelectedDetail()"></button>
          @if (canInstallSelected()) {
            <button
              pButton
              type="button"
              [label]="t('growth.industryPacks.install')"
              [loading]="actionKey() === selected()?.key && actionType() === 'install'"
              (click)="installSelected()"></button>
          }
          @if (canDisableSelected()) {
            <button
              pButton
              type="button"
              severity="danger"
              [label]="t('growth.industryPacks.disable')"
              [loading]="actionKey() === selected()?.key && actionType() === 'disable'"
              (click)="disableSelected()"></button>
          }
          @if (canUpgradeSelected()) {
            <button
              pButton
              type="button"
              severity="secondary"
              [label]="t('growth.industryPacks.upgrade')"
              [loading]="actionKey() === selected()?.key && actionType() === 'upgrade'"
              (click)="upgradeSelected()"></button>
          }
          <a class="link-btn" routerLink="/growth/marketplace/bindings">{{ t('growth.industryBindings.openList') }}</a>
        </div>
        <p-table
          [value]="filteredRows()"
          selectionMode="single"
          [selection]="selected()"
          (selectionChange)="onSelect($event)"
          dataKey="key">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('growth.industryPacks.columns.key') }}</th>
              <th>{{ t('growth.industryPacks.columns.displayName') }}</th>
              <th>{{ t('growth.industryPacks.columns.version') }}</th>
              <th>{{ t('growth.industryPacks.columns.installedVersion') }}</th>
              <th>{{ t('growth.industryPacks.columns.lifecycleStatus') }}</th>
              <th>{{ t('growth.industryPacks.columns.upgradeAvailable') }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr [pSelectableRow]="row">
              <td>
                <button type="button" class="link-btn" (click)="openDetail(row.key)">{{ row.key }}</button>
              </td>
              <td>{{ row.displayName }}</td>
              <td>{{ row.version }}</td>
              <td>{{ row.installedVersion ?? '-' }}</td>
              <td>{{ lifecycleLabel(row.lifecycleStatus) }}</td>
              <td>{{ row.catalogUpgradeAvailable ? t('common.yes') : t('common.no') }}</td>
            </tr>
          </ng-template>
        </p-table>
        @if (errorMessage()) {
          <p class="message error">{{ errorMessage() }}</p>
        }
      </section>
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px; background: #fff; }
    .list-card { display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; text-decoration: none; }
    .link-btn:hover { text-decoration: underline; }
    .message { margin: 0; font-size: 13px; }
    .message.error { color: #b91c1c; }
    .live-badge { font-size: 0.85rem; color: #64748b; }
  `]
})
export class GrowthIndustryPacksComponent implements OnInit {
  private readonly api = inject(GrowthAdminService);
  private readonly localization = inject(LocalizationService);
  private readonly router = inject(Router);
  private readonly context = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly liveStatusKey = liveStatusTranslationKey;

  readonly rows = signal<GrowthIndustryPackResponse[]>([]);
  readonly filteredRows = signal<GrowthIndustryPackResponse[]>([]);
  readonly selected = signal<GrowthIndustryPackResponse | null>(null);
  readonly errorMessage = signal('');
  readonly actionKey = signal<string | null>(null);
  readonly actionType = signal<'install' | 'disable' | 'upgrade' | null>(null);

  searchTerm = '';
  lifecycleFilter: string | null = null;

  private subscribedTopic: string | null = null;

  ngOnInit(): void {
    this.liveUpdateService.events$
      .pipe(filter(e => e.eventType === LiveUpdateEventTypes.growthIndustryChanged), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.load());
    this.destroyRef.onDestroy(() => {
      if (this.subscribedTopic) {
        void this.liveUpdateService.unsubscribe(this.subscribedTopic);
        this.subscribedTopic = null;
      }
    });
    void this.setupLiveSubscription();
    this.load();
  }

  lifecycleOptions(): Array<{ label: string; value: string }> {
    return GROWTH_INDUSTRY_PACK_LIFECYCLE_STATUSES.map(value => ({
      value,
      label: this.lifecycleLabel(value)
    }));
  }

  lifecycleLabel(value: string): string {
    return this.t(`growth.industryPacks.lifecycle.${value}`);
  }

  load(): void {
    this.errorMessage.set('');
    this.api.listIndustryPacks().subscribe({
      next: data => {
        this.rows.set(data);
        this.applyFilters();
      },
      error: () => {
        this.rows.set([]);
        this.filteredRows.set([]);
        this.selected.set(null);
        this.errorMessage.set(this.t('growth.industryPacks.messages.loadFailed'));
      }
    });
  }

  applyFilters(): void {
    const term = this.searchTerm.trim().toLowerCase();
    let filtered = this.rows();
    if (this.lifecycleFilter) {
      filtered = filtered.filter(item => item.lifecycleStatus === this.lifecycleFilter);
    }
    if (term) {
      filtered = filtered.filter(item =>
        item.key.toLowerCase().includes(term)
        || item.displayName.toLowerCase().includes(term)
        || (item.installedVersion ?? '').toLowerCase().includes(term));
    }
    this.filteredRows.set(filtered);
    const currentKey = this.selected()?.key;
    this.selected.set(filtered.find(item => item.key === currentKey) ?? filtered[0] ?? null);
  }

  onSelect(row: GrowthIndustryPackResponse | null): void {
    this.selected.set(row);
  }

  canInstallSelected(): boolean {
    const row = this.selected();
    return !!row && row.canInstall;
  }

  canDisableSelected(): boolean {
    return this.selected()?.lifecycleStatus === 'Installed';
  }

  canUpgradeSelected(): boolean {
    const row = this.selected();
    return !!row
      && row.catalogUpgradeAvailable
      && (row.lifecycleStatus === 'Installed' || row.lifecycleStatus === 'Disabled');
  }

  openSelectedDetail(): void {
    const row = this.selected();
    if (!row) {
      return;
    }
    this.openDetail(row.key);
  }

  openDetail(packKey: string): void {
    void this.router.navigate(['/growth/marketplace/installs', packKey]);
  }

  installSelected(): void {
    const row = this.selected();
    if (!row) {
      return;
    }
    this.runAction(row.key, 'install', () => this.api.installIndustryPack(row.key));
  }

  disableSelected(): void {
    const row = this.selected();
    if (!row) {
      return;
    }
    this.runAction(row.key, 'disable', () => this.api.disableIndustryPack(row.key));
  }

  upgradeSelected(): void {
    const row = this.selected();
    if (!row) {
      return;
    }
    this.runAction(row.key, 'upgrade', () => this.api.upgradeIndustryPack(row.key));
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private runAction(
    key: string,
    type: 'install' | 'disable' | 'upgrade',
    request: () => ReturnType<GrowthAdminService['installIndustryPack']>
  ): void {
    this.actionKey.set(key);
    this.actionType.set(type);
    this.errorMessage.set('');
    request().pipe(finalize(() => {
      this.actionKey.set(null);
      this.actionType.set(null);
    })).subscribe({
      next: updated => {
        this.rows.update(items => items.map(item => item.key === updated.key ? updated : item));
        this.applyFilters();
      },
      error: () => this.errorMessage.set(this.t(`growth.industryPacks.messages.${type}Failed`))
    });
  }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.context.context()?.tenantId;
    if (!tenantId) {
      return;
    }
    this.subscribedTopic = growthIndustryTopic(tenantId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }
}
